import type {
  Tag,
  GetTagsByIdsResponse,
  RecommendTagsResponse,
  SearchTagsResponse,
} from "./types";

// =============================================================================
// Get Tags By IDs Transformer
// =============================================================================

/**
 * Transforms the get tags by IDs API response into a flat array of Tag objects.
 * Uses the primary label for the tag name, falling back to the first label if no primary exists.
 * Sets score to 0.0 since tags are not matched to a specific headline.
 *
 * @param data - Response from GET /tags/ids/{ids}
 * @returns Array of Tag objects with hydrated names
 */
export const transformGetTagsByIdsResponse = (
  data: GetTagsByIdsResponse
): Tag[] => {
  return Object.entries(data)
    .map(([tagId, tagDetails]) => {
      const primaryLabel = tagDetails.labels?.find((label) => label.primary);
      const labelToUse = primaryLabel ?? tagDetails.labels?.[0];
      const name = labelToUse?.name ?? "";

      return {
        tag_id: Number(tagId),
        name,
        score: 0.0,
      };
    })
    .filter((tag) => tag.name);
};

// =============================================================================
// Recommend Tags Transformer
// =============================================================================

/**
 * Transforms the recommend tags API response into a flat array of Tag objects.
 * Filters tags by a minimum score threshold and uses the primary label for the tag name.
 *
 * @param data - Response from POST /tags/recommend
 * @param threshold - Minimum score threshold (default: 0.5)
 * @returns Array of Tag objects sorted by score (descending)
 */
export const transformRecommendTagsResponse = (
  data: RecommendTagsResponse,
  threshold: number = 0.5
): Tag[] => {
  const { scores, tags } = data;

  return Object.entries(scores)
    .filter(([, score]) => score >= threshold)
    .map(([tagId, score]) => {
      const tagData = tags[tagId];
      const primaryLabel = tagData?.labels?.find((label) => label.primary);
      const name = primaryLabel?.name ?? tagData?.labels?.[0]?.name ?? "";

      return {
        tag_id: Number(tagId),
        name,
        score,
      };
    })
    .filter((tag) => tag.name)
    .sort((a, b) => b.score - a.score);
};

// =============================================================================
// Search Tags Transformer
// =============================================================================

/**
 * Transforms the search tags API response into a flat array of Tag objects.
 * Uses the primary label for the tag name, falling back to the first label if no primary exists.
 * Uses the scanning_importance of the primary label as the score.
 *
 * @param data - Response from GET /tags/search/{query}
 * @returns Array of Tag objects
 */
export const transformSearchTagsResponse = (
  data: SearchTagsResponse
): Tag[] => {
  return data
    .map((tagItem) => {
      const primaryLabel = tagItem.labels?.find((label) => label.primary);
      const labelToUse = primaryLabel ?? tagItem.labels?.[0];
      const name = labelToUse?.name ?? "";
      const score = labelToUse?.scanning_importance ?? 1.0;

      return {
        tag_id: tagItem.id,
        name,
        score,
      };
    })
    .filter((tag) => tag.name);
};
